import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';

import { Subject, combineLatest, distinctUntilChanged, filter, fromEvent, map, take, takeUntil } from 'rxjs';

import { BreakpointService, PageConfigService, ScrollService } from '@hdms/shared/util-core';

import { MenuStateService } from './menu-state.service';

const MINIMIZE_SCROLL_TOP = 99;
const MINIMIZE_SCROLL_THRESHOLD = 20;
export const MINIMIZED_CLASS = 'header-minimized';
export const STICKY_TOP_TRANSITION_CLASS = 'sticky-top-transition';
export const FULL_TRANSITION_ANIMATION_CLASS = 'full-transition-animation';

@Injectable()
export class MinimizeHeaderService implements OnDestroy {
  public headerElement: HTMLElement;

  private isHeaderMinimized = false;

  private readonly renderer: Renderer2;
  private readonly unsubscribe$ = new Subject<void>();
  private readonly body = this.document.body;

  constructor(
    private readonly breakpointService: BreakpointService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly menuState: MenuStateService,
    private readonly pageConfig: PageConfigService,
    private readonly scrollService: ScrollService,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.subscribeToScroll();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public toggle(state: boolean, fullTransition = true): void {
    if (state === this.isHeaderMinimized) {
      return;
    }

    this.addTransitionClass();
    if (fullTransition) {
      this.toggleClass(FULL_TRANSITION_ANIMATION_CLASS, true, this.headerElement);
    }
    // force reflow
    this.forceReflow();
    this.toggleClass(MINIMIZED_CLASS, state);
    this.isHeaderMinimized = state;
    this.menuState.closeAll();
  }

  private subscribeToScroll(): void {
    const scroll$ = this.scrollService.scroll$.pipe(
      distinctUntilChanged((a, b) => Math.abs(b - a) < MINIMIZE_SCROLL_THRESHOLD)
    );

    combineLatest([scroll$, this.pageConfig.isCompressedPageLayout$, this.breakpointService.isMobileView$])
      .pipe(
        map(([scrollTop, isCompressed, isMobile]) => !isMobile && !isCompressed && scrollTop > MINIMIZE_SCROLL_TOP),
        filter(state => state !== this.isHeaderMinimized),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(state => this.toggle(state));
  }

  private addTransitionClass(): void {
    this.toggleClass(STICKY_TOP_TRANSITION_CLASS, true);

    fromEvent(this.headerElement, 'transitionend')
      .pipe(
        filter((e: TransitionEvent) => e.target === this.headerElement && e.propertyName === 'transform'),
        take(1),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(_ => {
        this.toggleClass(STICKY_TOP_TRANSITION_CLASS, false);
        this.toggleClass(FULL_TRANSITION_ANIMATION_CLASS, false, this.headerElement);
      });
  }

  private toggleClass(cls: string, state: boolean, element = this.body): void {
    this.renderer[state ? 'addClass' : 'removeClass'](element, cls);
  }

  private forceReflow(): number {
    return this.body.offsetHeight;
  }
}
